import type { Taxonomy, TaxonomyTerm } from '@/features/catalogs/taxonomies/model/taxonomy'
import type { ProductDetails, ProductSummary, ProductVariant, ProductImage } from '@/features/catalogs/products/api/productsApi'
import { termColumnKey } from '@/features/catalogs/taxonomies/store/taxonomiesSlice'

export interface UnpackedCatalogSnapshot {
  version: number
  deployedAt: string
  taxonomies: Taxonomy[]
  termColumns: Record<string, TaxonomyTerm[]>
  allTerms: TaxonomyTerm[]
  terms: TaxonomyTerm[]
  products: ProductSummary[]
  activeProducts: ProductSummary[]
  detailsById: Record<number, ProductDetails>
  productTermIds: Record<number, number[]>
}

export function unpackCatalogSnapshot(raw: any): UnpackedCatalogSnapshot {
  if (!raw || typeof raw !== 'object') {
    return {
      version: 1,
      deployedAt: '',
      taxonomies: [],
      termColumns: {},
      allTerms: [],
      terms: [],
      products: [],
      activeProducts: [],
      detailsById: {},
      productTermIds: {},
    }
  }

  // Handle data wrapper if envelope is present
  const data = raw.data && typeof raw.data === 'object' ? raw.data : raw

  // 1. Taxonomies (supports standard 'taxonomies' or minified 't')
  let taxonomies: Taxonomy[] = []
  if (Array.isArray(data.taxonomies)) {
    taxonomies = data.taxonomies
  } else if (Array.isArray(data.t)) {
    taxonomies = data.t.map((t: any) => ({
      id: t.i ?? t.id,
      code: t.c ?? t.code,
      name: t.n ?? t.name,
      description: t.d ?? t.description ?? null,
      position: t.p ?? t.position ?? 0,
    }))
  }

  // 2. Terms (supports standard 'terms' or minified 'm')
  let allTerms: TaxonomyTerm[] = []
  if (Array.isArray(data.terms)) {
    allTerms = data.terms
  } else if (Array.isArray(data.m)) {
    allTerms = data.m.map((m: any) => ({
      id: m.i ?? m.id,
      taxonomy_id: m.x ?? m.taxonomy_id,
      parent_id: m.p ?? m.parent_id ?? null,
      code: m.c ?? m.code,
      name: m.n ?? m.name,
      description: m.d ?? m.description ?? null,
      position: m.o ?? m.position ?? 0,
    }))
  }

  const termColumns: Record<string, TaxonomyTerm[]> = {}
  for (const term of allTerms) {
    const key = termColumnKey(term.taxonomy_id, term.parent_id)
    ;(termColumns[key] ??= []).push(term)
  }
  for (const terms of Object.values(termColumns)) {
    terms.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
  }

  const termsById = new Map<number, TaxonomyTerm>(allTerms.map((t) => [t.id, t]))
  const taxonomiesById = new Map<number, Taxonomy>(taxonomies.map((t) => [t.id, t]))

  // 3. Products (supports standard 'products' or minified 'p')
  const rawProducts = Array.isArray(data.products) ? data.products : Array.isArray(data.p) ? data.p : []
  const products: ProductSummary[] = []
  const detailsById: Record<number, ProductDetails> = {}
  const productTermIds: Record<number, number[]> = {}

  for (const p of rawProducts) {
    const id = p.id ?? p.i
    const slug = p.slug ?? p.s
    const name = p.name ?? p.n
    const summaryText = p.summary ?? p.u ?? null
    const status = p.status ?? p.st ?? 'active'
    const termIds = p.term_ids ?? p.t ?? []

    productTermIds[id] = termIds

    const summary: ProductSummary = {
      id,
      slug,
      name,
      summary: summaryText,
      status,
    }
    products.push(summary)

    const mappedTerms = termIds.map((termId: number) => {
      const term = termsById.get(termId)
      const tax = term ? taxonomiesById.get(term.taxonomy_id) : undefined
      return {
        term_id: termId,
        taxonomy_id: term?.taxonomy_id ?? 0,
        taxonomy_name: tax?.name ?? '',
        parent_id: term?.parent_id ?? null,
        code: term?.code ?? '',
        name: term?.name ?? '',
        position: term?.position ?? 0,
      }
    })

    const rawVariants = Array.isArray(p.variants) ? p.variants : Array.isArray(p.v) ? p.v : []
    const mappedVariants: ProductVariant[] = rawVariants.map((v: any) => {
      const rawOptions = Array.isArray(v.options) ? v.options : Array.isArray(v.o) ? v.o : []
      return {
        id: v.id ?? v.i,
        sku: v.sku ?? v.s ?? null,
        fulfillment_type: v.fulfillment_type ?? v.f ?? 'physical',
        price: v.price ?? v.p ?? 0,
        discount: v.discount ?? v.d ?? 0,
        status: v.status ?? v.st ?? 'active',
        options: rawOptions.map((opt: any) => ({
          option_id: opt.option_id ?? opt.i,
          option_value_id: opt.option_value_id ?? opt.v,
          option_name: opt.option_name ?? opt.n,
          value: opt.value ?? opt.val,
        })),
      }
    })

    const rawOptions = Array.isArray(p.options) ? p.options : Array.isArray(p.o) ? p.o : []
    const mappedOptions = rawOptions.map((opt: any) => ({
      id: opt.id ?? opt.i,
      code: opt.code ?? opt.c,
      name: opt.name ?? opt.n,
      applies_to: opt.applies_to ?? opt.a ?? 'both',
      position: opt.position ?? opt.p ?? 0,
      values: (opt.values || opt.v || []).map((val: any) => ({
        id: val.id ?? val.i,
        value: val.value ?? val.val,
        position: val.position ?? val.p ?? 0,
      })),
    }))

    const rawImages = Array.isArray(p.images) ? p.images : Array.isArray(p.img) ? p.img : []
    const mappedImages: ProductImage[] = rawImages.map((im: any) => ({
      id: im.id ?? im.i,
      product_id: id,
      image_url: im.image_url ?? im.u ?? '',
      alt: im.alt ?? im.a ?? null,
      position: im.position ?? im.p ?? 0,
      variant_ids: im.variant_ids ?? im.v ?? [],
      source_type: 'upload',
      image_key: null,
      external_url: null,
      created_at: 0,
      updated_at: 0,
    }))

    detailsById[id] = {
      ...summary,
      summary: summaryText,
      terms: mappedTerms,
      options: mappedOptions,
      variants: mappedVariants,
      images: mappedImages,
      videos: [],
    }
  }

  const activeProducts = products.filter((p) => p.status === 'active')

  return {
    version: data.version ?? data.v ?? 1,
    deployedAt: data.deployed_at ?? data.at ?? '',
    taxonomies,
    termColumns,
    allTerms,
    terms: allTerms,
    products,
    activeProducts,
    detailsById,
    productTermIds,
  }
}
